import { memo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { NodeStatus } from '../../../../generated/nodeDataTypes'

/**
 * Header-pill palette for `BaseNode` ONLY.
 *
 * This is NOT the node-border palette. Node card borders and any UI that must
 * match them (e.g. the variant picker's selected border) derive from
 * `AGENT_COLORS` in `src/constants/agentColors.ts`, keyed by agent identity.
 * These two palettes are unrelated: picking a key here by eye is how the
 * variant picker drifted to purple on the yellow character node and cyan on the
 * amber object node (post 1579940090429955). Do not reintroduce that coupling —
 * pass an `AgentColorConfig` instead.
 */
const ACCENT_COLORS = {
  purple: { bg: 'rgba(139, 92, 246, 0.2)', text: '#a78bfa', border: 'rgba(139, 92, 246, 0.3)', gradient: 'rgba(139, 92, 246, 0.12)' },
  blue:   { bg: 'rgba(59, 130, 246, 0.2)',  text: '#93c5fd', border: 'rgba(59, 130, 246, 0.3)',  gradient: 'rgba(59, 130, 246, 0.12)' },
  yellow: { bg: 'rgba(234, 179, 8, 0.2)',   text: '#fde047', border: 'rgba(234, 179, 8, 0.3)',   gradient: 'rgba(234, 179, 8, 0.12)' },
  green:  { bg: 'rgba(34, 197, 94, 0.2)',   text: '#86efac', border: 'rgba(34, 197, 94, 0.3)',   gradient: 'rgba(34, 197, 94, 0.12)' },
  orange: { bg: 'rgba(249, 115, 22, 0.2)',  text: '#fdba74', border: 'rgba(249, 115, 22, 0.3)',  gradient: 'rgba(249, 115, 22, 0.12)' },
  cyan:   { bg: 'rgba(6, 182, 212, 0.2)',   text: '#67e8f9', border: 'rgba(6, 182, 212, 0.3)',   gradient: 'rgba(6, 182, 212, 0.12)' },
}

export { ACCENT_COLORS }
export type AccentColor = keyof typeof ACCENT_COLORS

interface BaseNodeProps {
  title: string
  icon?: string
  selected?: boolean
  status?: NodeStatus
  children: ReactNode
  accentColor?: AccentColor
}

const STATUS_CONFIG: Record<NodeStatus, { dotColor: string; labelKey: string; glow: boolean }> = {
  idle:                      { dotColor: 'var(--text-muted)', labelKey: 'baseNode.statusReady',           glow: false },
  pending_execution:         { dotColor: 'var(--warning)',    labelKey: 'baseNode.statusQueued',           glow: true  },
  pending_cost_confirmation: { dotColor: 'var(--warning)',    labelKey: 'baseNode.statusPendingApproval',  glow: false },
  running:                   { dotColor: 'var(--warning)',    labelKey: 'baseNode.statusRunning',          glow: true  },
  completed:                 { dotColor: 'var(--success)',    labelKey: 'baseNode.statusDone',             glow: true  },
  error:                     { dotColor: 'var(--destructive)', labelKey: 'baseNode.statusError',           glow: true  },
}

export const BaseNode = memo(function BaseNode({
  title,
  icon,
  selected,
  status = 'idle',
  children,
  accentColor,
}: BaseNodeProps) {
  const { t } = useTranslation('canvas')
  const borderColor =
    status === 'error' ? 'border-destructive' :
    status === 'completed' ? 'border-success' :
    (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') ? 'border-warning' :
    selected ? 'border-primary/40' :
    'border-border-subtle'

  const shadowStyle = selected ? 'shadow shadow-primary/20' : 'shadow shadow-black/20'
  const accent = accentColor ? ACCENT_COLORS[accentColor] : null
  const statusCfg = STATUS_CONFIG[status]

  const isSpinning = status === 'running' || status === 'pending_execution'

  return (
    <div
      className={`
        base-node rounded-md border min-w-[280px] max-w-[400px]
        transition-colors
        ${borderColor}
        ${shadowStyle}
      `}
      style={{
        background: accent
          ? `radial-gradient(ellipse at 50% 100%, ${accent.gradient} 0%, transparent 60%), linear-gradient(180deg, var(--background) 0%, rgba(11,11,11,0.95) 100%)`
          : 'var(--background)',
      }}
    >
      {/* Header */}
      <div className="px-3 py-2.5 flex items-center justify-between rounded-t-xl border-b border-border-subtle">
        <div className="flex items-center gap-2">
          {icon && <span className="text-base">{icon}</span>}
          {accent ? (
            <span
              className="text-xs font-medium px-2.5 py-0.5 rounded-full"
              style={{
                background: accent.bg,
                color: accent.text,
                border: `1px solid ${accent.border}`,
              }}
            >
              {title}
            </span>
          ) : (
            <span className="font-semibold text-foreground/90 text-xs">{title}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isSpinning ? (
            <div
              className={`w-2 h-2 rounded-full border border-t-transparent ${
                status === 'running' ? 'animate-spin' : 'animate-spin-slow'
              }`}
              style={{
                borderColor: statusCfg.dotColor,
                borderTopColor: 'transparent',
                boxShadow: `0 0 6px ${statusCfg.dotColor}`,
              }}
            />
          ) : (
            <div
              className="w-2 h-2 rounded-full"
              style={{
                background: statusCfg.dotColor,
                boxShadow: statusCfg.glow ? `0 0 6px ${statusCfg.dotColor}` : 'none',
              }}
            />
          )}
          <span className="text-xxs text-muted-foreground">{t(statusCfg.labelKey)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="node-content p-3">
        {children}
      </div>
    </div>
  )
})

BaseNode.displayName = 'BaseNode'
